import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { gasApi, type GasPreset } from "@/api/gas";
import { CostCurveWidget } from "@/components/CostCurveWidget";
import { PeriodNav } from "@/components/PeriodNav";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  calendarRangeFor,
  fmt,
  fmtHourLocal,
  hourFromTs,
  monthLabel,
  todayIso,
  type Period,
} from "@/lib/format";

/** Сторінка «Газ» (FR-UI-GAS): факт із лічильника поруч із паспортною кривою.
 *  Доба календарна київська (00:00–24:00), а не газова доба порталу 07:00–07:00. */
export function GasPage() {
  const { t, i18n } = useTranslation();
  const [end, setEnd] = useState(todayIso());
  const [period, setPeriod] = useState<Period>("month");
  const [openDay, setOpenDay] = useState<string | null>(null);

  const { from, to } = useMemo(() => calendarRangeFor(end, period), [end, period]);

  const summary = useQuery({
    queryKey: ["gas-summary", from, to],
    queryFn: () => gasApi.summary(from, to),
  });

  // Через `?? []` кожен рендер давав би новий масив і зривав меморізацію нижче.
  const days = useMemo(() => summary.data?.days ?? [], [summary.data]);

  const totals = useMemo(() => {
    let gas = 0;
    let gen = 0;
    let hours = 0;
    for (const d of days) {
      gas += d.gas_m3 ?? 0;
      gen += d.generation_kwh ?? 0;
      hours += d.generation_hours;
    }
    return { gas, gen, hours, specific: gen > 0 ? (gas / gen) * 1000 : null };
  }, [days]);

  const chart = useMemo(
    () =>
      days.map((d) => ({
        day: d.day.slice(5),
        gas: d.gas_m3 ?? 0,
        power: d.generation_hours > 0 ? (d.generation_kwh ?? 0) / d.generation_hours : 0,
      })),
    [days],
  );

  const starts = useMemo(() => summary.data?.starts ?? [], [summary.data]);

  /** Крива й факт на одному полі: спільна вісь потужності. */
  const curveChart = useMemo(() => {
    const s = summary.data;
    if (!s) return [];
    const byPower = new Map<number, { power_kw: number; curve?: number; fact?: number }>();
    for (const p of s.curve) {
      byPower.set(p.power_kw, { power_kw: p.power_kw, curve: p.gas_m3h });
    }
    for (const p of s.fact_points) {
      const row = byPower.get(p.power_kw) ?? { power_kw: p.power_kw };
      row.fact = p.gas_m3h;
      byPower.set(p.power_kw, row);
    }
    return Array.from(byPower.values()).sort((a, b) => a.power_kw - b.power_kw);
  }, [summary.data]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("gas.title")}</CardTitle>
          <CardDescription>{t("gas.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <PeriodNav
            period={period}
            refDate={end}
            onPeriodChange={setPeriod}
            onRefDateChange={setEnd}
          />
        </CardContent>
      </Card>

      {/* Режими роботи: факт проти паспорта */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(summary.data?.presets ?? []).map((p) => (
          <PresetCard key={p.label} preset={p} />
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label={t("gas.total_gas")} value={`${fmt(totals.gas, 1)} ${t("gas.m3")}`} />
        <Stat
          label={t("gas.prepaid_remaining")}
          value={
            summary.data?.prepaid_remaining_m3 === null ||
            summary.data?.prepaid_remaining_m3 === undefined
              ? "—"
              : `${fmt(summary.data.prepaid_remaining_m3, 1)} ${t("gas.m3")}`
          }
        />
        {/* Середня ціна пулу — на кінець вибраного періоду, тобто на його місяць. */}
        <Stat
          label={`${t("gas.avg_price")} (${monthLabel(to, i18n.language)})`}
          value={
            summary.data?.prepaid_avg_price_uah_per_m3 === null ||
            summary.data?.prepaid_avg_price_uah_per_m3 === undefined
              ? "—"
              : `${fmt(summary.data.prepaid_avg_price_uah_per_m3, 4)} ${t("gas.uah_m3")}`
          }
        />
        <Stat label={t("gas.total_hours")} value={fmt(totals.hours)} />
        <Stat
          label={t("gas.specific")}
          value={totals.specific === null ? "—" : `${fmt(totals.specific, 1)} ${t("gas.m3_mwh")}`}
        />
      </div>

      {/* Добова таблиця з розгортанням у години */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("gas.table_title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {days.length === 0 ? (
            <div className="text-sm text-slate-500">{t("home1.no_data")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="p-2 w-8" />
                    <th className="p-2">{t("gas.col_day")}</th>
                    <th className="p-2 text-right">{t("gas.col_hours")}</th>
                    <th className="p-2 text-right">{t("gas.col_gas")}</th>
                    <th className="p-2 text-right">{t("gas.col_generation")}</th>
                    <th className="p-2 text-right">{t("gas.col_specific")}</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => {
                    const open = openDay === d.day;
                    const spec =
                      d.generation_kwh && d.gas_m3 !== null
                        ? (d.gas_m3 / d.generation_kwh) * 1000
                        : null;
                    return (
                      <Fragment key={d.day}>
                        <tr
                          className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                          onClick={() => setOpenDay(open ? null : d.day)}
                        >
                          <td className="p-2 text-slate-400">
                            {open ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </td>
                          <td className="p-2">{d.day}</td>
                          <td className="p-2 text-right font-mono">{d.generation_hours}</td>
                          <td className="p-2 text-right font-mono">{fmt(d.gas_m3, 1)}</td>
                          <td className="p-2 text-right font-mono">{fmt(d.generation_kwh)}</td>
                          <td className="p-2 text-right font-mono">{fmt(spec, 1)}</td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={6} className="bg-slate-50 p-0">
                              <HourlyTable date={d.day} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Пуски КГУ */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("gas.starts_title")}</CardTitle>
          <CardDescription>{t("gas.starts_hint")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label={t("gas.starts_count")} value={fmt(starts.length)} />
            <Stat
              label={t("gas.starts_gas")}
              value={`${fmt(summary.data?.starts_total_m3 ?? null, 1)} ${t("gas.m3")}`}
            />
            <Stat
              label={t("gas.starts_cost")}
              value={`${fmt(summary.data?.starts_total_uah ?? null)} ${t("common.uah")}`}
            />
            <Stat
              label={t("gas.starts_avg_cost")}
              value={
                starts.length === 0 || summary.data?.starts_total_uah == null
                  ? "—"
                  : `${fmt(summary.data.starts_total_uah / starts.length)} ${t("common.uah")}`
              }
            />
            <Stat
              label={t("gas.starts_avg")}
              value={
                starts.length === 0
                  ? "—"
                  : `${fmt(
                      (summary.data?.starts_total_m3 ?? 0) / starts.length,
                      1,
                    )} ${t("gas.m3")}`
              }
            />
          </div>

          {summary.data?.full_load_rate_m3h != null && (
            <div className="text-xs text-slate-500">
              {t("gas.full_load_rate")}:{" "}
              <span className="font-mono">
                {fmt(summary.data.full_load_rate_m3h, 1)} {t("gas.m3_h")}
              </span>
            </div>
          )}

          {starts.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="p-2">{t("gas.col_start")}</th>
                    <th className="p-2 text-right">{t("gas.col_ramp")}</th>
                    <th className="p-2 text-right">{t("gas.col_gas")}</th>
                    <th className="p-2 text-right">{t("gas.col_start_gas")}</th>
                    <th className="p-2 text-right">{t("gas.col_start_cost")}</th>
                  </tr>
                </thead>
                <tbody>
                  {starts.map((s) => (
                    <tr key={s.ts} className="border-t border-slate-100">
                      <td className="p-2 font-mono">
                        {s.ts.slice(0, 10)} {fmtHourLocal(hourFromTs(s.ts))}
                      </td>
                      <td className="p-2 text-right font-mono">{s.hours}</td>
                      <td className="p-2 text-right font-mono">{fmt(s.gas_m3, 1)}</td>
                      <td className="p-2 text-right font-mono font-semibold">
                        {fmt(s.start_m3, 1)}
                      </td>
                      <td className="p-2 text-right font-mono">{fmt(s.cost_uah)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Дві панелі зі спільною віссю часу: різні одиниці не можна класти
          на одне поле — перетини ліній тоді нічого не означають. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("gas.chart_title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ChartPanel
            title={`${t("gas.avg_power")}, ${t("common.kw")}`}
            data={chart}
            dataKey="power"
            color="#2a78d6"
          />
          <ChartPanel
            title={`${t("gas.gas")}, ${t("gas.m3")}`}
            data={chart}
            dataKey="gas"
            color="#eb6834"
          />
        </CardContent>
      </Card>

      {/* Газова крива: паспорт проти факту */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("gas.curve_title")}</CardTitle>
          <CardDescription>{t("gas.curve_hint")}</CardDescription>
        </CardHeader>
        <CardContent>
          {curveChart.length === 0 ? (
            <div className="text-sm text-slate-500">{t("home1.no_data")}</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={curveChart} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="power_kw"
                    type="number"
                    domain={["dataMin", "dataMax"]}
                    tickFormatter={(v) => fmt(v as number)}
                    label={{ value: t("optimizer.axis_power"), position: "insideBottom", offset: -10 }}
                  />
                  <YAxis tickFormatter={(v) => fmt(v as number)} />
                  <Tooltip formatter={(v) => fmt(v as number, 1)} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="curve"
                    name={t("gas.curve_passport")}
                    stroke="#838d9e"
                    strokeDasharray="5 4"
                    dot={false}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="fact"
                    name={t("gas.curve_fact")}
                    stroke="#eb6834"
                    strokeWidth={2}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Той самий віджет, що й на «Головній 1», але з фактичною собівартістю
          з лічильника газу поруч із паспортною кривою. */}
      <CostCurveWidget date={to} factPoints={summary.data?.fact_cost_points} />
    </div>
  );
}

function PresetCard({ preset }: { preset: GasPreset }) {
  const { t } = useTranslation();
  const diff = preset.diff_pct;
  const diffClass =
    diff === null ? "text-slate-400" : diff > 0 ? "text-red-600" : "text-emerald-600";
  return (
    <Card>
      <CardContent className="p-4 space-y-1">
        <div className="text-xs text-slate-500">
          {preset.power_pct}% — {fmt(preset.power_kw)} {t("common.kw")}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-mono font-semibold">
            {preset.fact_m3h === null ? "—" : fmt(preset.fact_m3h, 1)}
          </span>
          <span className="text-xs text-slate-500">{t("gas.m3_h")}</span>
        </div>
        <div className="text-xs text-slate-500">
          {t("gas.by_curve")}: <span className="font-mono">{fmt(preset.curve_m3h, 1)}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className={diffClass}>
            {diff === null ? "—" : `${diff > 0 ? "+" : ""}${fmt(diff, 1)}%`}
          </span>
          {/* Без кількості годин середнє неможливо інтерпретувати. */}
          <span className="text-slate-400">
            {t("gas.hours_in_sample", { count: preset.hours })}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartPanel({
  title,
  data,
  dataKey,
  color,
}: {
  title: string;
  data: Record<string, number | string>[];
  dataKey: string;
  color: string;
}) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-slate-500">{title}</div>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" />
            <YAxis tickFormatter={(v) => fmt(v as number)} />
            <Tooltip formatter={(v) => fmt(v as number, 1)} />
            <Bar dataKey={dataKey} fill={color} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function HourlyTable({ date }: { date: string }) {
  const { t } = useTranslation();
  const hourly = useQuery({
    queryKey: ["gas-hourly", date],
    queryFn: () => gasApi.hourly(date),
  });

  if (hourly.isLoading) {
    return <div className="p-3 text-sm text-slate-500">{t("common.loading")}</div>;
  }
  const rows = hourly.data ?? [];
  if (rows.length === 0) {
    return <div className="p-3 text-sm text-slate-500">{t("home1.no_data")}</div>;
  }
  return (
    <div className="p-3">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-slate-500">
            <th className="p-1.5 w-16">{t("common.hour")}</th>
            <th className="p-1.5 text-right">{t("gas.col_power")}</th>
            <th className="p-1.5 text-right">{t("gas.col_gas")}</th>
            <th className="p-1.5 text-right">{t("gas.col_specific")}</th>
            <th className="p-1.5 text-right">{t("gas.col_cost")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.ts} className="border-t border-slate-200">
              <td className="p-1.5 font-mono">{fmtHourLocal(hourFromTs(r.ts))}</td>
              <td className="p-1.5 text-right font-mono">{fmt(r.generation_kwh)}</td>
              <td className="p-1.5 text-right font-mono">{fmt(r.gas_m3, 1)}</td>
              <td className="p-1.5 text-right font-mono">{fmt(r.gas_m3_per_mwh, 1)}</td>
              <td className="p-1.5 text-right font-mono">{fmt(r.cost_uah)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-xl font-mono font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}
